import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, User, Plane, Upload, FileSearch, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NationalitySelect from "../components/NationalitySelect";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: "Personal Details", icon: User },
  { id: 2, label: "Travel Info", icon: Plane },
  { id: 3, label: "Documents", icon: Upload },
  { id: 4, label: "Review & Submit", icon: FileSearch },
];

export default function ApplyPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countries, setCountries] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    destination_country_code: searchParams.get("destination") || "", visa_type: searchParams.get("type") || "tourist",
    processing_type: searchParams.get("processing") || "standard", full_name: "", email: "", phone: "",
    passport_number: "", nationality: searchParams.get("nationality") || "", travel_date: "", purpose: "tourism", notes: "",
  });
  const [files, setFiles] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdApp, setCreatedApp] = useState(null);
  const [savedDocs, setSavedDocs] = useState([]);
  const [useSaved, setUseSaved] = useState(false);
  const [savedSelected, setSavedSelected] = useState({});

  useEffect(() => { axios.get(`${API}/countries`).then(r => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user && !form.full_name) setForm(f => ({ ...f, full_name: user.name || "", email: user.email || "" })); }, [user]);
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem("token");
      axios.get(`${API}/saved-documents`,  { headers: { Authorization: `Bearer ${token}` } }).then(r => setSavedDocs(r.data)).catch(() => {});
    }
  }, [user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedCountry = countries.find(c => c.country_code === form.destination_country_code);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50">
      <div className="text-center"><h2 className="heading-font text-2xl font-bold text-slate-900 mb-4">Sign in to Apply</h2>
        <p className="text-sm text-slate-500 mb-6">Create an account to submit visa applications.</p>
        <Link to="/login" className="text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-lg inline-block">Sign In</Link></div>
    </div>
  );

  const canNext = () => {
    if (step === 1) return form.full_name && form.email && form.phone && form.passport_number;
    if (step === 2) return form.destination_country_code && form.travel_date && form.nationality;
    return true;
  };

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API}/applications`, form,  { headers: { Authorization: `Bearer ${token}` } });
      // Upload files if any
      for (const [docType, file] of Object.entries(files)) {
        const fd = new FormData(); fd.append("application_id", data.id); fd.append("document_type", docType); fd.append("file", file);
        await axios.post(`${API}/documents/upload`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      }
      setCreatedApp(data);
    } catch (err) { setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Submission failed."); }
    finally { setLoading(false); }
  };

  if (createdApp) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md" data-testid="application-success">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
        <h2 className="heading-font text-2xl font-bold text-slate-900 mb-3">Application Created!</h2>
        <p className="text-sm text-slate-500 mb-6">Your {selectedCountry?.country_name || form.destination_country_code} visa application is ready.</p>
        <div className="flex flex-col gap-3">
          <button onClick={() => navigate(`/apply/${createdApp.id}/payment`)} className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700">Pay & Submit</button>
          <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-500 hover:text-blue-600">Go to Dashboard</button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step > s.id ? "step-done" : step === s.id ? "step-active" : "step-pending"}`}>
                {step > s.id ? <Check className="w-5 h-5" /> : s.id}
              </div>
              <span className={`hidden sm:block ml-2 text-xs font-medium ${step >= s.id ? "text-slate-800" : "text-slate-400"}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className={`w-8 md:w-16 h-0.5 mx-2 ${step > s.id ? "bg-emerald-400" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
            className="card-elevated rounded-2xl p-7">

            {/* Step 1: Personal Details */}
            {step === 1 && (
              <div className="space-y-5" data-testid="step-personal">
                <h2 className="heading-font text-xl font-bold text-slate-900 mb-1">Personal Details</h2>
                <p className="text-sm text-slate-400 mb-4">Enter your details as they appear on your passport</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Full Name *</label>
                    <Input data-testid="apply-name" value={form.full_name} onChange={e => set("full_name", e.target.value)} placeholder="As on passport" className="h-11 rounded-lg border-slate-200" /></div>
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Email *</label>
                    <Input data-testid="apply-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} className="h-11 rounded-lg border-slate-200" /></div>
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Phone *</label>
                    <Input data-testid="apply-phone" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1 234 567 890" className="h-11 rounded-lg border-slate-200" /></div>
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Passport Number *</label>
                    <Input data-testid="apply-passport" value={form.passport_number} onChange={e => set("passport_number", e.target.value)} placeholder="AB1234567" className="h-11 rounded-lg border-slate-200" /></div>
                </div>
              </div>
            )}

            {/* Step 2: Travel Info */}
            {step === 2 && (
              <div className="space-y-5" data-testid="step-travel">
                <h2 className="heading-font text-xl font-bold text-slate-900 mb-1">Travel Information</h2>
                <p className="text-sm text-slate-400 mb-4">Tell us about your trip</p>
                <div><label className="text-sm text-slate-600 mb-1 block font-medium">Destination *</label>
                  <Select value={form.destination_country_code} onValueChange={v => set("destination_country_code", v)}>
                    <SelectTrigger data-testid="apply-country" className="w-full h-11 border-slate-200 rounded-lg"><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-60">
                      {countries.map(c => <SelectItem key={c.country_code} value={c.country_code}><span className="flex items-center gap-2">{c.flag} {c.country_name}</span></SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Nationality *</label>
                    <NationalitySelect value={form.nationality} onValueChange={v => set("nationality", v)} /></div>
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Travel Date *</label>
                    <Input data-testid="apply-date" type="date" value={form.travel_date} onChange={e => set("travel_date", e.target.value)} className="h-11 rounded-lg border-slate-200" /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Visa Type</label>
                    <Select value={form.visa_type} onValueChange={v => set("visa_type", v)}>
                      <SelectTrigger className="w-full h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="tourist">Tourist</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="student">Student</SelectItem>
                      </SelectContent></Select></div>
                  <div><label className="text-sm text-slate-600 mb-1 block font-medium">Processing Speed</label>
                    <div className="flex gap-2 mt-1">
                      {["standard", "express", "urgent"].map(p => (
                        <button key={p} type="button" onClick={() => set("processing_type", p)}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-semibold transition-all ${form.processing_type === p ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                      ))}
                    </div></div>
                </div>
              </div>
            )}

            {/* Step 3: Documents */}
            {step === 3 && (
              <div className="space-y-5" data-testid="step-documents">
                <h2 className="heading-font text-xl font-bold text-slate-900 mb-1">Upload Documents</h2>
                <p className="text-sm text-slate-400 mb-4">Upload required documents or use saved ones from your profile</p>

                {/* Auto-fill toggle */}
                {savedDocs.length > 0 && (
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100 mb-2" data-testid="use-saved-docs">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><FileSearch className="w-4 h-4 text-blue-600" /></div>
                      <div>
                        <p className="text-sm font-semibold text-blue-800">Use saved documents</p>
                        <p className="text-xs text-blue-500">{savedDocs.length} document(s) found in your profile</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => { setUseSaved(!useSaved); if (!useSaved) { const mapped = {}; savedDocs.forEach(d => { mapped[d.document_type] = d; }); setSavedSelected(mapped); } else { setSavedSelected({}); } }}
                      className={`w-12 h-7 rounded-full transition-all relative ${useSaved ? "bg-blue-600" : "bg-slate-300"}`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-1 transition-all ${useSaved ? "left-6" : "left-1"}`} />
                    </button>
                  </div>
                )}

                {["passport", "photo", "bank_statement"].map(dt => {
                  const isSavedAttached = useSaved && savedSelected[dt];
                  return (
                    <div key={dt} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isSavedAttached ? "bg-emerald-50 border-emerald-200" : files[dt] ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-100"}`}>
                      <div className="flex items-center gap-3">
                        {(isSavedAttached || files[dt]) ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <Upload className="w-5 h-5 text-slate-400" />}
                        <div>
                          <p className="text-sm font-medium text-slate-700">{dt.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                          <p className="text-xs text-slate-400">{isSavedAttached ? `From profile: ${savedSelected[dt].original_name || "Saved"}` : files[dt] ? files[dt].name : "PDF, JPG, PNG"}</p>
                        </div>
                      </div>
                      {!isSavedAttached && (
                        <label className="cursor-pointer text-xs font-semibold text-blue-600 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-50">
                          {files[dt] ? "Change" : "Upload"}
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => e.target.files[0] && setFiles(f => ({ ...f, [dt]: e.target.files[0] }))} />
                        </label>
                      )}
                      {isSavedAttached && <span className="text-xs text-emerald-600 font-medium px-3 py-1 bg-emerald-100 rounded-full">Auto-filled</span>}
                    </div>
                  );
                })}
                <p className="text-xs text-slate-400">Documents can also be uploaded after creating the application.</p>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-5" data-testid="step-review">
                <h2 className="heading-font text-xl font-bold text-slate-900 mb-1">Review & Submit</h2>
                <p className="text-sm text-slate-400 mb-4">Verify your details before submitting</p>
                {error && <div data-testid="apply-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>}
                <div className="space-y-3">
                  {[["Full Name", form.full_name], ["Email", form.email], ["Phone", form.phone], ["Passport", form.passport_number],
                    ["Nationality", form.nationality], ["Destination", selectedCountry?.country_name || form.destination_country_code],
                    ["Visa Type", form.visa_type], ["Processing", form.processing_type], ["Travel Date", form.travel_date],
                    ["Documents", Object.keys(files).length ? Object.keys(files).join(", ") : "None uploaded yet"]
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-slate-400">{l}</span><span className="text-sm text-slate-800 font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
            className="text-sm text-slate-500 px-5 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 4 ? (
            <button onClick={() => setStep(s => Math.min(4, s + 1))} disabled={!canNext()}
              data-testid="apply-next" className="text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading || !form.destination_country_code}
              data-testid="apply-submit" className="text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
              {loading ? "Submitting..." : <><span>Submit Application</span><ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
